import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import SFetch from '@app/utils/sfetch'
import api from '@app/insights/api'

export interface ReconciliationText {
  id: number
  name: string
  workflow_name?: string
  // Normalised from the API's partner_reconciliation_text_id so every filter
  // dropdown shares one partner-ref key (see utils/partner-editing.ts).
  partnerTemplateId?: number
  [key: string]: unknown
}

export const useReconciliationTextsStore = defineStore(
  'insights-reconciliation-texts',
  () => {
    const reconciliationTexts = ref<ReconciliationText[] | null>(null)
    const reconciliationTextNamedResults = ref<unknown[]>([])

    async function fetchReconciliationTexts(): Promise<void> {
      if (reconciliationTexts.value !== null) return

      const data =
        (await api.fetchReconciliationTexts()) as ReconciliationText[]
      reconciliationTexts.value = data.map((rt) => ({
        ...rt,
        partnerTemplateId: rt.partner_reconciliation_text_id as
          | number
          | undefined,
      }))
    }

    async function fetchReconciliationTextNamedResults(
      reconciliationTextId: number
    ): Promise<void> {
      const url =
        window.Silverfin.Routes.insights_recon_filter_identifiers_reconciliation_text_path(
          window.Silverfin.currentFirmId,
          reconciliationTextId
        )

      const data = await SFetch.jsonWithAlert<{
        insightsReconFilterIdentifiers: unknown[]
      }>(url, { method: 'GET' })
      if (data) {
        reconciliationTextNamedResults.value =
          data.insightsReconFilterIdentifiers
      }
    }

    const reconciliationTextsById = computed(() => {
      const byId = new Map<number, ReconciliationText>()
      for (const rt of reconciliationTexts.value ?? []) {
        if (!byId.has(rt.id)) byId.set(rt.id, rt)
      }
      return byId
    })

    function getReconciliationTextById(
      id: number
    ): ReconciliationText | undefined {
      return reconciliationTextsById.value.get(id)
    }

    return {
      reconciliationTexts,
      reconciliationTextNamedResults,
      fetchReconciliationTexts,
      fetchReconciliationTextNamedResults,
      getReconciliationTextById,
    }
  }
)
